import { Pim } from '@mummssoftware/utils';
import { getFormListV2 } from '@mummssoftware/utils/pimReader';
import { isElectionAddendumForm } from '@mummssoftware/utils/utils';

export type ElectionAddendumFormListRow = {
  form_name: string;
  form_id: number;
  formkey: string;
  formtechtype: string;
  isstickyallowed?: boolean;
  issandboxform?: boolean;
  patientmanifest_id?: number | null;
  statusforpatientperson?: string | null;
  isvisitrequired?: boolean;
  iscriteriamet?: boolean;
};

type ResolveArgs = {
  keycloak: Keycloak.KeycloakInstance;
  pim: Pim;
  agency: string;
  currentUser: mumms.User;
  patient: mumms.patient;
  levelOfCare?: string | null;
};

type CacheEntry = {
  cacheKey: string;
  form: ElectionAddendumFormListRow;
};

let sessionCache: CacheEntry | null = null;

const buildCacheKey = ({
  agency,
  patientNumber,
  personNumber,
  levelOfCare,
}: {
  agency: string;
  patientNumber: string;
  personNumber?: string | number | null;
  levelOfCare?: string | null;
}) => `${agency}|${patientNumber}|${personNumber ?? ''}|${levelOfCare || ''}`;

/**
 * Resolve Election Addendum from getformlist_v2 by formkey.
 * Caches the matching row for the session (same patient/agency/LOC/user).
 * Propagates FormListV2Error from getFormListV2; returns null if form not in list.
 */
export const resolveElectionAddendumForm = async ({
  keycloak,
  pim,
  agency,
  currentUser,
  patient,
  levelOfCare,
}: ResolveArgs): Promise<ElectionAddendumFormListRow | null> => {
  const cacheKey = buildCacheKey({
    agency,
    patientNumber: patient.patientNumber,
    personNumber: currentUser.personNumber ?? '',
    levelOfCare,
  });

  if (sessionCache?.cacheKey === cacheKey) {
    return sessionCache.form;
  }

  const forms = await getFormListV2({
    keycloak,
    pim,
    agency,
    currentUser,
    patient,
    levelOfCare,
  });

  if (!forms) {
    return null;
  }

  const formRows = (forms as unknown) as ElectionAddendumFormListRow[];
  const match = formRows.find((form) => isElectionAddendumForm(form)) ?? null;

  if (!match) {
    return null;
  }

  sessionCache = { cacheKey, form: match };
  return match;
};
